import { useCallback } from 'react'
import { useAuth, dpopFetch, getExistingKeyPair } from 'headlo-auth'

// DPoP-attaching fetch hook — required for any data-plane call against a
// V2-token endpoint. Ported verbatim from headlo-desktop; consolidating
// into headlo-auth itself is the eventual home (see the reference
// implementation comment in the desktop copy).
//
// Why not just headlo-auth's useAuthFetch: that only sets
// Authorization: Bearer. V2 tokens carry cnf.jkt binding, and the worker's
// A.5.1 middleware requires a matching DPoP proof header or returns
// 401 dpop_missing. This hook composes three primitives headlo-auth
// exports (getToken + getExistingKeyPair + dpopFetch) into a
// fetch-compatible wrapper.
//
// Tech debt: identical logic in headlo-desktop/src/renderer/useDpopFetch.ts.
// Not a big deal — 30 lines, no divergence risk — but a future extract to
// headlo-auth would DRY it. Filed under "when headlo-auth adds a proper
// useAuthFetchV2."
export function useDpopFetch(): typeof fetch {
  const { getToken, publishableKey } = useAuth()

  return useCallback<typeof fetch>(async (input, init) => {
    const token   = await getToken()
    // Phase 1 (2026-09-07): pk-scoped keys — pass publishableKey so we
    // read the right slot. Falls back to legacy 'session_key' inside
    // getExistingKeyPair when the pk-scoped slot is empty AND the legacy
    // slot has a value (migration for pre-Phase-1 users).
    const keyPair = await getExistingKeyPair(publishableKey)

    // Not signed in or no key — fall back to plain fetch. Endpoint will
    // return 401 which callers handle as unauthenticated.
    if (!token || !keyPair) return fetch(input, init)

    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url

    return dpopFetch(url, {
      ...init,
      keyPair,
      accessToken: token,
      method: init?.method ?? (input instanceof Request ? input.method : 'GET'),
    })
  }, [getToken, publishableKey])
}
